import { Row, Col, Space } from 'components-antd';
import styles from './styles.module.scss';
import Card from './Card';
import { useHistory } from 'react-router-dom';
import { routes } from 'settings/navigation/routes';
import { useDispatch, useSelector } from 'react-redux';
import { getUnreadThreadsCount } from 'store/selectors/sockets/threads';
import { DRAWER_MESSAGES_TYPES } from 'settings/constants/drawers';
import { setOtherRolesQuotesTabIndexAction } from 'store/actions/quotes';
import { lazy, useEffect, useState, Suspense } from 'react';
import { getPartnerStats } from 'api/partners';
import TasksModal from './components/TasksModal';
import { getTaskAggregate } from 'api/taskAggregate';
import { tasksStatusesIds } from 'settings/constants/transactionTasks';
import { Spinner } from 'components';
import {
  changeMessagesDrawerTypeAction,
  openMessagesDrawerAction,
} from 'store/actions/drawers/messages';
import {
  getTransactionEffect,
  getTransactionTaskEffect,
  resetTransactionTaskEffect,
  setTransactionTaskParamsEffect,
  setTransactionTasksLastStateEffect,
} from 'store/effects';

const ViewRevamped = lazy(
  () =>
    import(
      'pages/Workshop/Transactions/TransactionTasks/components/Task/ViewRevamped/ViewRevamped'
    ),
);

export const PartnerDashboard = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const unreadThreadsCount = useSelector(getUnreadThreadsCount);
  const [loading, setLoading] = useState<boolean>(true);
  const [stats, setStats] = useState<any>(undefined);
  const [showTasksModal, setShowTasksModal] = useState<boolean>(false);
  const [tasksLoading, setTaskLoading] = useState<boolean>(false);
  const [tasksToComplete, setTasksToComplete] = useState<any[]>([]);
  const [showViewModal, setShowViewModal] = useState<boolean>(false);

  useEffect(() => {
    fetchPartnerStats();
    () => {
      dispatch(
        setTransactionTaskParamsEffect({
          campaignId: null,
          isTaskForm: false,
          isTaskDetail: false,
        }),
      );
      dispatch(resetTransactionTaskEffect());
    };
  }, []);

  const fetchPartnerStats = async () => {
    try {
      const response = await getPartnerStats();
      if (response.status === 200 && response?.data?.result) setStats(response?.data?.result);
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  };

  const handleUnreadMessages = () => {
    unreadThreadsCount > 0 && dispatch(openMessagesDrawerAction(true));
    dispatch(
      changeMessagesDrawerTypeAction({
        type: DRAWER_MESSAGES_TYPES.UNREAD,
      }),
    );
  };

  const fetchTasksToComplete = async () => {
    setTaskLoading(true);
    try {
      const cfg = {
        filters: {
          assignedByIds: [],
          assignedToIds: [],
          campaignOnly: false,
          dueFrom: null,
          dueTo: null,
          projectStatuses: [],
          projects: [],
          statuses: [
            tasksStatusesIds.new,
            tasksStatusesIds.inProgress,
            tasksStatusesIds.stuck,
            tasksStatusesIds.overdue,
          ],
          tags: [],
          transactionStatuses: [],
          transactions: [],
        },
      };
      const response = await getTaskAggregate(cfg);
      if (response.status === 200 && response.data?.result?.length)
        setTasksToComplete(response.data.result);
    } catch (e) {
      setTasksToComplete([]);
    }
    setTaskLoading(false);
  };

  const handleTaskViewClick = (task) => {
    const { TransactionId: transactionId, Id: taskId, CampaignId: campaignId } = task;
    transactionId &&
      dispatch(getTransactionEffect({ id: transactionId, include: 'tasks,participants' }));
    dispatch(
      setTransactionTaskParamsEffect({
        taskId,
        transactionId,
        campaignId,
        isTaskDetail: true,
      }),
    );
    dispatch(getTransactionTaskEffect({ transactionId, taskId }));
    dispatch(setTransactionTasksLastStateEffect({ taskId }));
    setShowTasksModal(false);
    setShowViewModal(true);
  };

  const onCloseModal = () => {
    setShowViewModal(false);
    setShowTasksModal(true);
    dispatch(
      setTransactionTaskParamsEffect({ campaignId: null, isTaskForm: false, isTaskDetail: false }),
    );
    dispatch(resetTransactionTaskEffect());
  };

  return (
    <div className={styles.mainContentWrapper}>
      <header className={styles.dashboardHeader}>
        <h1 className={styles.heading}>Dashboard</h1>
      </header>
      <div className={styles.dashboardCardArea}>
        <Space direction="vertical" size="middle" className={styles.quoteSpace}>
          <Row justify="space-around" align="top" gutter={24}>
            <Col xs={24} md={12} lg={8}>
              <Card
                heading="New Inquiries"
                count={stats?.NewInquiries || 0}
                color="#928CDA"
                clickable={!!stats?.NewInquiries}
                loading={loading}
                handleClick={() => {
                  dispatch(setOtherRolesQuotesTabIndexAction(0));
                  history.push(`${routes.servicesInquiries}?groupId=New&src=dashboard`);
                }}
              />
              <Card
                heading="Unread Messages"
                count={unreadThreadsCount || 0}
                color="#FF576D"
                clickable={!!unreadThreadsCount}
                loading={false}
                handleClick={handleUnreadMessages}
              />
            </Col>
            <Col xs={24} md={12} lg={8}>
              <Card
                heading="Pending Inquiries"
                count={stats?.PendingInquiries || 0}
                color="#4673D1"
                clickable={!!stats?.PendingInquiries}
                loading={loading}
                handleClick={() => {
                  dispatch(setOtherRolesQuotesTabIndexAction(1));
                  history.push(
                    `${routes.servicesInquiries}?groupId=Pending Response&src=dashboard`,
                  );
                }}
              />
              <Card
                heading="Total Inquiries"
                count={(stats?.NewInquiries || 0) + (stats?.PendingInquiries || 0)}
                color="#00B152"
                clickable={!!((stats?.NewInquiries || 0) + (stats?.PendingInquiries || 0))}
                loading={loading}
                handleClick={() => {
                  dispatch(setOtherRolesQuotesTabIndexAction(0));
                  history.push(`${routes.servicesInquiries}?groupId=New&src=dashboard`);
                }}
              />
            </Col>
            <Col xs={24} md={12} lg={8}>
              <Card
                heading="Tasks"
                count={stats?.PendingTasksCount || 0}
                color="#FB913A"
                clickable={!!stats?.PendingTasksCount}
                loading={loading}
                handleClick={() => {
                  fetchTasksToComplete();
                  setShowTasksModal(true);
                }}
              />
              <Card
                heading="Profile Views"
                count={stats?.ProfileViews || 0}
                color="#51BFE1"
                clickable={false}
                loading={loading}
              />
            </Col>
          </Row>
        </Space>
        <TasksModal
          loading={tasksLoading}
          handleClose={() => setShowTasksModal(false)}
          tasks={tasksToComplete}
          open={showTasksModal}
          handleTaskViewClick={handleTaskViewClick}
        />
        <Suspense fallback={<Spinner />}>
          <ViewRevamped isOpen={showViewModal} onCloseModal={onCloseModal} className="" />
        </Suspense>
      </div>
    </div>
  );
};
